/** Store-content rails only. Shared Worlds / Maps is a separate hero picker. */

export type StoreDensity = 2 | 3 | 4 | 5;
export type StoreSort = 'newest' | 'rating' | 'popular' | 'price';
export type RailKind = 'category' | 'map';
/** Genre chosen when the map is uploaded. Short title-line label, not the parenthetical examples. */
export type MapGenre = 'fantasy' | 'science-fiction' | 'modern-horror';

export const MAP_GENRE_LABEL: Record<MapGenre, string> = {
  fantasy: 'Fantasy',
  'science-fiction': 'Science Fiction',
  'modern-horror': 'Modern & Horror',
};

export type ContentKind =
  | 'Establishment'
  | 'Adventure'
  | 'Campaign'
  | 'One Shot Encounter'
  | 'Side Quest'
  | 'Mini Campaign / Adventure Arc'
  | 'Map';

export interface StoreFilters {
  type?: ContentKind;
  types?: ContentKind[];
  sort: StoreSort;
  world?: string;
  biome?: string;
  gamesystem?: string;
  priceMax?: number;
  freeOnly?: boolean;
  playtimeMax?: number;
  minRating?: number;
  author?: string;
}

export interface StoreItem {
  id: string;
  title: string;
  image: string;
  type: ContentKind;
  author: string;
  world: string;
  worldKey: string;
  rating: number;
  reviews: number;
  price: number;
  playtimeHrs: number;
  party: string;
  desc: string;
  gamesystem: string;
  biome: string;
  publishedAt: string;
  /** The signed-in user's own listing — shown, but not purchasable by them. */
  isMine?: boolean;
  /** Already bought / claimed by the signed-in user. */
  isOwned?: boolean;
}

export interface AccessibleMap {
  key: string;
  name: string;
  genre: MapGenre;
}

export interface RailIntent {
  id: string;
  kind: RailKind;
  titleLead: string;
  titleGold: string;
  subtitle: string;
  filters: StoreFilters;
  createType?: ContentKind;
  baseScore: number;
  density?: StoreDensity;
  genre?: MapGenre;
  mapKey?: string;
}

export interface DeviceBudget {
  maxRails: number;
  densities: StoreDensity[];
  allowTwoRows: boolean;
}

export interface ComposerSignals {
  userId: string | number | null;
  planName: string;
  purchasedTypes: ContentKind[];
  searchFacets: Partial<StoreFilters>[];
  activeWorldKey: string;
  activeWorldName: string;
  /** Maps the visitor can see. Public shared worlds today; later the user's unlocked maps. */
  accessibleMaps: AccessibleMap[];
}

export interface ComposedRail {
  id: string;
  kind: RailKind;
  titleLead: string;
  titleGold: string;
  subtitle: string;
  density: StoreDensity;
  rows: 1 | 2;
  filters: StoreFilters;
  seeAllParams: Record<string, string>;
  items: StoreItem[];
  createPlaceholders: number[];
  createType?: ContentKind;
  genre?: MapGenre;
  genreLabel?: string;
  mapKey?: string;
}

export type LandingBlock =
  | { kind: 'rail'; rail: ComposedRail }
  | { kind: 'featured-map' };

export interface WorldAdventure {
  type: string;
  storeStatus: string;
  name: string;
  author: string;
  region: string;
  rating: string;
  reviews: string;
  desc: string;
  playtime: string;
  party: string;
  price: string;
  image?: string;
}

export interface WorldPin {
  id: string;
  name: string;
  top: string;
  left: string;
  featured?: boolean;
  adventure: WorldAdventure;
}

export interface SharedWorld {
  key: string;
  name: string;
  image: string;
  gamesystem: string;
  genre: MapGenre;
  pinsPlaced: number;
  contributors: number;
  gamesRunning: number;
  pitch: string;
  pins: WorldPin[];
}
